import { useEffect, useRef, useState } from "react";
import {
  Activity,
  AlertCircle,
  AlertOctagon,
  CheckSquare,
  List,
  Truck,
  Type,
} from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Card, CardContent } from "semantic-ui-react";
import {
  addAndUpload,
  deleteDocumentList,
  updateAndUpload,
} from "../../api/documentUpload";
import { addVehicle, updateVehicle } from "../../api/vehicleapi";
import { FORMS, MODULES, searchTagConst } from "../../const";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import useNextAndPreviousAuthority from "../../hooks/useNextAndPreviousAuthority";
import { errorActions } from "../../store/error-slice";
import DocumentUpload from "../DocumentUpload";
import Tab from "../Tab";
import UIMessage from "../UIMessage";
import Button from "../input/Button";
import { validateVehicleDocuments } from "../validations/documentRequirementHelper";
import { vehicleValidation } from "../validations/vehicleCreation";
import VehicleAccidents from "./VehicleAccidents";
import VehicleCheckups from "./VehicleCheckups";
import VehicleForm from "./VehicleForm";
import VehicleOperations from "./VehicleOperations";
import VehicleTypes from "./VehicleTypes";
import Modal from "../Modal";

const AddVehicle = ({
  editMode,
  setEditMode,
  currentValues,
  dropdown,
  isLoading,
  searchTag,
  onClose,
  isOpen,
}) => {
  const user = useSelector((state) => state.user);

  const { firstViableTab, lastViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.VEHICLE,
  });
  const [tab, setTab] = useState(firstViableTab);

  const { next, prev } = useNextAndPreviousAuthority({
    sectionKey: "vehicle",
    minTab: 1,
    maxTab: 6,
    module: MODULES.Master,
    section: FORMS.VEHICLE,
    currentTab: tab,
    authenticated: true,
  });

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const errorState = useSelector((state) => state.error.vehicleErrors);
  const dispatch = useDispatch();
  const [submitted, setSubmitted] = useState(false);

  const vehicleRef = useRef();
  const operationRef = useRef();
  const typeRef = useRef();
  const checkupRef = useRef();
  const documentRef = useRef();
  const accidentRef = useRef();
  const [vehOperationalStatus, setVehOperationalStatus] = useState(null);

  const deleteWarningRef = useRef();
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);

  console.log("Edit Mode Vehicle -> ", editMode);

  const reset = () => {
    vehicleRef.current?.resetForm();
    operationRef.current?.resetGrid();
    typeRef.current?.resetGrid();
    checkupRef.current?.resetGrid();
    accidentRef.current?.resetGrid();
    documentRef.current?.resetForm?.();
    dispatch(errorActions.clearVehicleError());
    setEditMode(false);
  };

  useEffect(() => {
    if (!editMode && (!currentValues || Object.keys(currentValues).length === 0)) {
      reset();
    }
  }, [editMode, currentValues, isOpen]);

  useEffect(() => {
    if (editMode && currentValues?.vehicleDetail) {
      setVehOperationalStatus(currentValues.vehicleDetail.vehOperationalStatus);
    }
  }, [editMode, currentValues]);

  const handleSubmit = async (e, bypassDeleteCheck) => {
    e.preventDefault();
    if (!bypassDeleteCheck && vehicleRef.current.isDeletedActive()) {
      setPendingSubmitEvent(e);
      setShowDeleteWarning(true);
      deleteWarningRef.current?.showModal();
      return;
    }
    setIsSubmitLoading(true);
    setSubmitted(true);

    const vehicleDetails = await vehicleRef.current.getData();
    setVehOperationalStatus(vehicleDetails.vehOperationalStatus);
    const vehicleErrors = vehicleValidation(vehicleDetails, dropdown?.inspectionTimeOpt);
    const typeErrors = typeRef.current.validateForm();
    const checkupErrors = checkupRef.current.validateForm();

    if (Object.keys(vehicleErrors).length > 0) {
      dispatch(
        errorActions.validateVehicle({
          vehicleData: vehicleDetails,
          inspectionTimeOpt: dropdown?.inspectionTimeOpt,
        }),
      );
      toast.error("There was an error adding Vehicle", {
        autoClose: 5000,
        containerId: "Main",
      });
      setIsSubmitLoading(false);
      return;
    } else {
      dispatch(errorActions.clearVehicleError());
    }

    if (typeErrors) {
      dispatch(
        errorActions.addVehicleErrorObject({
          form: "typeErrors",
          errors: { vetType: "At least one vehicle type must be added." },
        }),
      );
      toast.error("There should have at least one Vehicle type", {
        autoClose: 5000,
        containerId: "Main",
      });
      setIsSubmitLoading(false);
      return;
    } else {
      dispatch(errorActions.clearVehicleError());
    }

    if (checkupErrors) {
      dispatch(
        errorActions.addVehicleErrorObject({
          form: "checkupErrors",
          errors: {
            vckType: "At least one vehicle checkup type must be added.",
          },
        }),
      );
      toast.error("There should be at least one Vehicle checkup type", {
        autoClose: 5000,
        containerId: "Main",
      });
      setIsSubmitLoading(false);
      return;
    } else {
      dispatch(errorActions.clearVehicleError());
    }


    const hasRLExpiry = checkupRef.current?.hasValidRevenueLicenseExpiry?.(
      dropdown?.vckTypeOpt,
    );
    if (!hasRLExpiry) {
      dispatch(
        errorActions.addVehicleErrorObject({
          form: "checkupErrors",
          errors: {
            vckExpiry:
              "Revenue License checkup with a valid Expiry Date is required.",
          },
        }),
      );
      toast.error(
        "Revenue License checkup with a valid Expiry Date is required.",
        {
          autoClose: 5000,
          containerId: "Main",
        },
      );
      setIsSubmitLoading(false);
      return;
    }

    const uploadedVehicleDocs =
      documentRef.current?.getAllDocuments?.() ||
      documentRef.current?.getData() ||
      [];
    const vehicleDocValidation = validateVehicleDocuments(
      uploadedVehicleDocs,
      vehOperationalStatus,
      dropdown?.fileNameOptions,
    );

    if (!vehicleDocValidation.isValid) {
      dispatch(
        errorActions.addVehicleErrorObject({
          form: "documentUploadErrors",
          errors: vehicleDocValidation.errors,
        }),
      );
      toast.error(
        `Please upload all mandatory documents: ${vehicleDocValidation.missingDocs.join(", ")}`,
        { autoClose: 6000, containerId: "Main" },
      );
      setIsSubmitLoading(false);
      return;
    } else {
      dispatch(
        errorActions.addVehicleErrorObject({
          form: "documentUploadErrors",
          errors: {},
        }),
      );
    }

    try {
      if (editMode) {
        const response = await updateVehicle({
          vehicleDetail: vehicleDetails,
          vehicleOperation: operationRef.current.getData(),
          vehicleMappedType: typeRef.current.getData(),
          vehicleCheckup: checkupRef.current.getData(),
          vehicleAccident: accidentRef.current.getData(),
          author: user.username,
        });

        if (response.data?.httpStatus === "ALREADY_REPORTED") {
          toast.error(response.data.errMessage, {
            autoClose: 5000,
            containerId: "Main",
          });
          setIsSubmitLoading(false);
          return;
        }
        const documents = documentRef.current.getData();
        let hasUploadErrors = false;

        for (let doc of documents.created) {
          const { file, ...rest } = doc;
          const formData = new FormData();
          formData.append("multipartFile", doc.file);
          formData.append("documentRequest", JSON.stringify(rest));
          formData.append("isImage", rest.docCategory === 1);
          const res = await addAndUpload(formData);
          if (res && res?.responseCode === "2040") {
            toast.success(res?.message, {
              autoClose: 5000,
              containerId: "Main",
            });
          } else {
            hasUploadErrors = true;
            documentRef.current.markUploadFailed(doc.id ?? doc.fileName);
            toast.error(
              res?.message || `Error adding document ${doc.docName}`,
              {
                autoClose: 5000,
                containerId: "Main",
              },
            );
          }
        }

        for (let doc of documents.modified) {
          const { file, ...rest } = doc;
          const formData = new FormData();
          formData.append("multipartFile", doc.file);
          formData.append("documentRequest", JSON.stringify(rest));
          formData.append("isImage", rest.docCategory === 1);
          const res = await updateAndUpload(formData);
          if (res && res?.responseCode === "2040") {
            toast.success(res?.message, {
              autoClose: 5000,
              containerId: "Main",
            });
          } else {
            hasUploadErrors = true;
            documentRef.current.markUploadFailed(doc.id ?? doc.fileName);
            toast.error(
              res?.message || `Error updating document ${doc.docName}`,
              {
                autoClose: 5000,
                containerId: "Main",
              },
            );
          }
        }

        if (documents?.deleted.length > 0) {
          await deleteDocumentList({
            docIdList: documents?.deleted,
          });
          toast.success("Documents deleted successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });
        }
        toast.success("Vehicle Updated successfully!", {
          autoClose: 5000,
          containerId: "Main",
        });
        if (hasUploadErrors) {
          setIsSubmitLoading(false);
          return;
        }
        onClose();
        reset();
      } else {
        const response = await addVehicle({
          vehicleDetail: vehicleDetails,
          vehicleOperation: operationRef.current.getData(),
          vehicleMappedType: typeRef.current.getData(),
          vehicleCheckup: checkupRef.current.getData(),
          vehicleAccident: accidentRef.current.getData(),
          author: user.username,
        });
        if (response?.errId === 1) {
          toast.error(response.errMessage + " !", {
            autoClose: 5000,
            containerId: "Main",
          });
        } else {
          const documents = documentRef.current.getData();
          let hasUploadErrors = false;
          for (let doc of documents) {
            const { file, ...rest } = doc;
            const formData = new FormData();
            formData.append("multipartFile", doc.file);
            formData.append(
              "documentRequest",
              JSON.stringify({
                ...rest,
                docReference: response.data[0],
              }),
            );
            formData.append("isImage", rest.docCategory === 1);

            const res = await addAndUpload(formData);
            if (res && res?.responseCode === "2040") {
              toast.success(res?.message, {
                autoClose: 5000,
                containerId: "Main",
              });
            } else {
              hasUploadErrors = true;
              documentRef.current.markUploadFailed(doc.id ?? doc.fileName);
              toast.error(
                res?.message || `Error adding document ${doc.docName}`,
                {
                  autoClose: 5000,
                  containerId: "Main",
                },
              );
            }
          }
          toast.success("Vehicle added successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });

          if (hasUploadErrors) {
            setIsSubmitLoading(false);
            return;
          }
        }
      }
    } catch (error) {
      toast.error("There was an error adding Vehicle", {
        autoClose: 5000,
        containerId: "Main",
      });
    }

    setIsSubmitLoading(false);
  };

  const vehicleForm = (
    <VehicleForm
      isLoading={isLoading}
      ref={vehicleRef}
      errors={errorState.vehicleErrors}
      dropdown={{
        featureDropdown: dropdown.featureDropdown,
        fuelTypeOpt: dropdown.fuelTypeOpt,
        tyreSizeOpt: dropdown.tyreSizeOpt,
        weightUOMOpt: dropdown.uomOptions,
        sizeUOMOpt: dropdown.uomOptions,
        bodyTypeOpt: dropdown.bodyTypeOpt,
        statusOpt: dropdown.statusOpt,
        activeOpt: dropdown.activeOpt,
        inspectionTimeOpt: dropdown.inspectionTimeOpt,
        yesNoOpt: dropdown.yesNoOptions,
        vehAuthorizedOpt: dropdown.vehAuthorizedOpt,
        inspecGroupOpt: dropdown.inspecGroupOpt,
        vehRateTypeOpt: dropdown.vehRateTypeOpt,
        vetUOMOpt: dropdown.uomOptions,
        inscoverageOpt: dropdown.inscoverageOpt,
        specialStatusOpt: dropdown.specialStatusOpt,
      }}
      editMode={editMode}
      onOperationalStatusChange={setVehOperationalStatus}
      currentValues={
        editMode && currentValues !== null ? currentValues.vehicleDetail : null
      }
      onClose={onClose}
    />
  );

  const vehicleOperation = (
    <VehicleOperations
      isLoading={isLoading}
      ref={operationRef}
      dropdown={{
        vpyCategoryOpt: dropdown.vpyCategoryOpt,
        statusOpt: dropdown.statusOpt,
        activeOpt: dropdown.activeOpt,
      }}
      editMode={editMode}
      currentValues={
        editMode && currentValues !== null
          ? currentValues.vehicleOperation
          : null
      }
    />
  );

  const vehicleType = (
    <VehicleTypes
      isLoading={isLoading}
      ref={typeRef}
      dropdown={{
        vetUOMOpt: dropdown.uomOptions,
        activeOpt: dropdown.activeOpt,
      }}
      editMode={editMode}
      currentValues={
        editMode && currentValues !== null
          ? currentValues.vehicleMappedType
          : null
      }
    />
  );

  const vehicleCheckup = (
    <VehicleCheckups
      isLoading={isLoading}
      ref={checkupRef}
      dropdown={{
        vckTypeOpt: dropdown.vckTypeOpt,
        vckConditionOpt: dropdown.vckConditionOpt,
        statusOpt: dropdown.statusOpt,
        activeOpt: dropdown.activeOpt,
      }}
      editMode={editMode}
      currentValues={
        editMode && currentValues !== null ? currentValues.vehicleCheckup : null
      }
    />
  );

  const documentUpload = (
    <DocumentUpload
      isLoading={isLoading}
      opeType={4}
      ref={documentRef}
      reference={
        currentValues
          ? currentValues.vehicleDetail
            ? currentValues.vehicleDetail.vehId
            : null
          : null
      }
      dropdown={{
        documentTypeOptions: dropdown.documentTypeOptions,
        extensionOptions: dropdown.extensionOptions,
        statusOptions: dropdown.statusOpt,
        fileNameOptions: dropdown.fileNameOptions,
        activeOptions: dropdown.activeOpt,
      }}
      editMode={editMode}
      currentValues={
        editMode && currentValues !== null ? currentValues.document : null
      }
    />
  );

  const vehicleAccident = (
    <VehicleAccidents
      isLoading={isLoading}
      dropdown={{
        vacTypeOpt: dropdown.vacTypeOpt,
        activeOpt: dropdown.activeOpt,
        breakdownCategoryOpt: dropdown.breakdownCategoryOpt,
        jobOpt: dropdown.jobOpt,
        accidentStatusOpt: dropdown.accidentStatusOpt,
        statusOpt: dropdown.statusOpt,
      }}
      ref={accidentRef}
      editMode={editMode}
      reference={
        currentValues
          ? currentValues.vehicleDetail
            ? currentValues.vehicleDetail.vehId
            : null
          : null
      }
      currentValues={
        editMode && currentValues ? currentValues.vehicleAccident : null
      }
    />
  );

  let tabObj = [];

  switch (searchTag) {
    case searchTagConst.vehicle.VEHICLE:
    case searchTagConst.vehicle.VEHICLE_CONFIG: {
      tabObj = [
        {
          tab: (
            <Tab
              Icon={Truck}
              onClick={() => {
                setTab(1);
              }}
              active={tab === 1}
              errors={errorState.hasErrors}
              label="Vehicle"
              authenticated={true}
              resizeForSmallerScreens={true}
              form={FORMS.VEHICLE.VEH_DETAIL.form}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 1 ? "block" : "none" }}
            >
              {vehicleForm}
            </div>
          ),
        },
      ];
      break;
    }
    case searchTagConst.vehicle.VEHICLE_DOCS: {
      tabObj = [
        {
          tab: (
            <Tab
              Icon={Truck}
              onClick={() => {
                setTab(1);
              }}
              active={tab === 1}
              errors={errorState.hasErrors}
              label="Vehicle"
              authenticated={true}
              form={FORMS.VEHICLE.VEH_DETAIL.form}
              resizeForSmallerScreens={true}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 1 ? "block" : "none" }}
            >
              {vehicleForm}
            </div>
          ),
        },
        {
          tab: (
            <Tab
              Icon={List}
              onClick={() => {
                setTab(2);
              }}
              active={tab === 2}
              label="Document"
              authenticated={true}
              resizeForSmallerScreens={true}
              form={FORMS.VEHICLE.DOCUMENT.form}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 2 ? "block" : "none" }}
            >
              {documentUpload}
            </div>
          ),
        },
      ];
      break;
    }
    case searchTagConst.vehicle.VEHICLE_TYPES: {
      tabObj = [
        {
          tab: (
            <Tab
              Icon={Truck}
              onClick={() => {
                setTab(1);
              }}
              active={tab === 1}
              errors={errorState.hasErrors}
              label="Vehicle"
              authenticated={true}
              resizeForSmallerScreens={true}
              form={FORMS.VEHICLE.VEH_DETAIL.form}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 1 ? "block" : "none" }}
            >
              {vehicleForm}
            </div>
          ),
        },
        {
          tab: (
            <Tab
              Icon={Type}
              onClick={() => {
                setTab(2);
              }}
              active={tab === 2}
              label="Types"
              authenticated={true}
              resizeForSmallerScreens={true}
              form={FORMS.VEHICLE.TYPE.form}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 2 ? "block" : "none" }}
            >
              {vehicleType}
            </div>
          ),
        },
      ];
      break;
    }
    case searchTagConst.vehicle.VEHICLE_OPERATIONS: {
      tabObj = [
        {
          tab: (
            <Tab
              Icon={Truck}
              onClick={() => {
                setTab(1);
              }}
              active={tab === 1}
              errors={errorState.hasErrors}
              label="Vehicle"
              authenticated={true}
              form={FORMS.VEHICLE.VEH_DETAIL.form}
              resizeForSmallerScreens={true}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 1 ? "block" : "none" }}
            >
              {vehicleForm}
            </div>
          ),
        },
        {
          tab: (
            <Tab
              Icon={AlertOctagon}
              onClick={() => {
                setTab(2);
              }}
              active={tab === 2}
              label="Operations"
              authenticated={true}
              resizeForSmallerScreens={true}
              form={FORMS.VEHICLE.OPERATIONS.form}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 2 ? "block" : "none" }}
            >
              {vehicleOperation}
            </div>
          ),
        },
      ];
      break;
    }
    case searchTagConst.vehicle.VEHICLE_CHECKUP: {
      tabObj = [
        {
          tab: (
            <Tab
              Icon={Truck}
              onClick={() => {
                setTab(1);
              }}
              active={tab === 1}
              errors={errorState.hasErrors}
              label="Vehicle"
              authenticated={true}
              resizeForSmallerScreens={true}
              form={FORMS.VEHICLE.VEH_DETAIL.form}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 1 ? "block" : "none" }}
            >
              {vehicleForm}
            </div>
          ),
        },
        {
          tab: (
            <Tab
              Icon={CheckSquare}
              onClick={() => {
                setTab(2);
              }}
              active={tab === 2}
              label="Checkups"
              authenticated={true}
              resizeForSmallerScreens={true}
              form={FORMS.VEHICLE.CHECKUP.form}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 2 ? "block" : "none" }}
            >
              {vehicleCheckup}
            </div>
          ),
        },
      ];
      break;
    }
    default: {
      tabObj = [
        {
          tab: (
            <Tab
              Icon={Truck}
              onClick={() => {
                setTab(1);
              }}
              active={tab === 1}
              errors={Object.keys(errorState.vehicleErrors).length > 0}
              label="Vehicle"
              authenticated={true}
              form={FORMS.VEHICLE.VEH_DETAIL.form}
              resizeForSmallerScreens={true}
              showMandatory={true}
              isMandatory={true}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 1 ? "block" : "none" }}
            >
              {vehicleForm}
            </div>
          ),
        },
        {
          tab: (
            <Tab
              Icon={AlertOctagon}
              onClick={() => {
                setTab(2);
              }}
              active={tab === 2}
              label="Operations"
              authenticated={true}
              resizeForSmallerScreens={true}
              form={FORMS.VEHICLE.OPERATIONS.form}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 2 ? "block" : "none" }}
            >
              {vehicleOperation}
            </div>
          ),
        },
        {
          tab: (
            <Tab
              Icon={Type}
              onClick={() => {
                setTab(3);
              }}
              active={tab === 3}
              label="Types"
              errors={Object.keys(errorState.typeErrors).length > 0}
              authenticated={true}
              form={FORMS.VEHICLE.TYPE.form}
              resizeForSmallerScreens={true}
              showMandatory={true}
              isMandatory={true}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 3 ? "block" : "none" }}
            >
              {vehicleType}
            </div>
          ),
        },
        {
          tab: (
            <Tab
              Icon={CheckSquare}
              onClick={() => {
                setTab(4);
              }}
              active={tab === 4}
              label="Checkups"
              authenticated={true}
              errors={Object.keys(errorState.checkupErrors).length > 0}
              resizeForSmallerScreens={true}
              showMandatory={true}
              isMandatory={true}
              form={FORMS.VEHICLE.CHECKUP.form}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 4 ? "block" : "none" }}
            >
              {vehicleCheckup}
            </div>
          ),
        },
        {
          tab: (
            <Tab
              Icon={List}
              onClick={() => {
                setTab(5);
              }}
              active={tab === 5}
              label="Document"
              authenticated={true}
              resizeForSmallerScreens={true}
              form={FORMS.VEHICLE.DOCUMENT.form}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 5 ? "block" : "none" }}
            >
              {documentUpload}
            </div>
          ),
        },
        {
          tab: (
            <Tab
              Icon={Activity}
              onClick={() => {
                setTab(6);
              }}
              active={tab === 6}
              label="Breakdowns"
              authenticated={true}
              resizeForSmallerScreens={true}
              form={FORMS.VEHICLE.BREAKDOWN.form}
            />
          ),
          content: (
            <div
              className="ui bottom attached active tab segment"
              style={{ display: tab === 6 ? "block" : "none" }}
            >
              {vehicleAccident}
            </div>
          ),
        },
      ];
    }
  }

  const nextButtonHandler = async () => {
    switch (tab) {
      case 1: {
        const vehErrors = vehicleRef.current.validateForm();
        if (Object.keys(vehErrors).length > 0) {
          dispatch(
            errorActions.addVehicleErrorObject({
              form: "vehicleErrors",
              errors: vehErrors,
            }),
          );
          return;
        } else {
          dispatch(errorActions.clearVehicleError());
        }
        break;
      }
      case 3: {
        const isPrimaryAvailable = typeRef.current.validatePrimary();
        if (!isPrimaryAvailable) {
          toast.error(
            "At least one vehicle type must be marked as Primary (Yes).",
            { autoClose: 5000, containerId: "Main" },
          );
          return;
        }
        const typeErrors = typeRef.current.validateForm();
        if (typeErrors) {
          dispatch(
            errorActions.addVehicleErrorObject({
              form: "typeErrors",
              errors: { vetType: "At least one vehicle type must be added." },
            }),
          );
          toast.error("There should have at least one Vehicle type", {
            autoClose: 5000,
            containerId: "Main",
          });
          return;
        } else {
          dispatch(errorActions.clearVehicleError());
        }
        break;
      }
      case 4: {
        const checkupErrors = checkupRef.current.validateForm();
        if (checkupErrors) {
          dispatch(
            errorActions.addVehicleErrorObject({
              form: "checkupErrors",
              errors: {
                vckType:
                  "At least one vehicle checkup type must be added.",
              },
            }),
          );
          toast.error("There should be at least one Vehicle checkup type", {
            autoClose: 5000,
            containerId: "Main",
          });
          return;
        }

        const hasRLExpiry = checkupRef.current?.hasValidRevenueLicenseExpiry?.(
          dropdown?.vckTypeOpt,
        );
        if (!hasRLExpiry) {
          dispatch(
            errorActions.addVehicleErrorObject({
              form: "checkupErrors",
              errors: {
                vckExpiry:
                  "Revenue License checkup with a valid Expiry Date is required.",
              },
            }),
          );
          toast.error(
            "Revenue License checkup with a valid Expiry Date is required.",
            {
              autoClose: 5000,
              containerId: "Main",
            },
          );
          return;
        } else {
          dispatch(errorActions.clearVehicleError());
        }
        break;
      }
      case 5: {
        if (vehOperationalStatus === 3) {
          const checkupErrors = checkupRef.current.validateForm();
          if (checkupErrors) {
            dispatch(
              errorActions.addVehicleErrorObject({
                form: "checkupErrors",
                errors: {
                  vckType:
                    "At least one vehicle checkup type must be added when vehicle status is Ad-Hoc",
                },
              }),
            );
            toast.error("There should be at least one Vehicle checkup type", {
              autoClose: 5000,
              containerId: "Main",
            });
            return;
          } else {
            dispatch(errorActions.clearVehicleError());
          }
        }
        break;
      }
      default:
    }
    setTab(next());
  };
  const deleteWarningModal = (
      <div style={{ padding: "20px" }}>
        <h3 style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AlertCircle color="red" size="25" />
          <p>Warning</p>
        </h3>
        <p>Active is set to Deleted. Are you sure you want to proceed?</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <Button
            label="Cancel"
            onClick={() => {
              deleteWarningRef.current?.close();
              setShowDeleteWarning(false);
              setPendingSubmitEvent(null);
            }}
          />
          <Button
            label="Proceed"
            className="ui red button"
            onClick={() => {
              deleteWarningRef.current?.close();
              setShowDeleteWarning(false);
              handleSubmit(pendingSubmitEvent, true);
              setPendingSubmitEvent(null);
            }}
          />
        </div>
      </div>
    );

  return (<>
    <form className="ui form" onSubmit={handleSubmit}>
      <Card style={{ width: "80vw", boxShadow: "none" }}>
        <CardContent
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div className="ui tabular menu top attached">
              {tabObj.map((tab) => tab.tab)}
            </div>
            {tabObj.map((tab) => tab.content)}
          </div>
          {submitted && errorState.hasErrors && (
            <UIMessage type="negative" width="100%" centered={false}>
              <div className="content">
                <div className="header">
                  There were some errors with your submission
                </div>
                {errorState.vehicleErrors &&
                  Object.keys(errorState.vehicleErrors).length > 0 && (
                    <p>
                      <div className="header">Vehicle</div>
                      <div className="content">
                        <ul>
                          {Object.entries(errorState.vehicleErrors).map(
                            ([_, value]) => (
                              <li key={value}>{value}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    </p>
                  )}
                {errorState.typeErrors &&
                  Object.keys(errorState.typeErrors).length > 0 && (
                    <p>
                      <div className="header">Vehicle Types</div>
                      <div className="content">
                        <ul>
                          {Object.entries(errorState.typeErrors).map(
                            ([_, value]) => (
                              <li key={value}>{value}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    </p>
                  )}
                {errorState.checkupErrors &&
                  Object.keys(errorState.checkupErrors).length > 0 && (
                    <p>
                      <div className="header">Checkups</div>
                      <div className="content">
                        <ul>
                          {Object.entries(errorState.checkupErrors).map(
                            ([_, value]) => (
                              <li key={value}>{value}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    </p>
                  )}
              </div>
            </UIMessage>
          )}
          <div
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              {tabObj.length > 1 && tab > firstViableTab && (
                <Button
                  template="back"
                  tab={tab - 1}
                  module={MODULES.Master}
                  section={FORMS.VEHICLE}
                  authenticated={true}
                  onClick={() => setTab(prev())}
                />
              )}
              {tabObj.length > 1 && tab < lastViableTab && (
                <Button
                  template="next"
                  tab={tab + 1}
                  module={MODULES.Master}
                  section={FORMS.VEHICLE}
                  authenticated={true}
                  onClick={nextButtonHandler}
                />
              )}
              {editMode ? (
                <Button
                  type="submit"
                  template="create"
                  isLoading={isSubmitLoading}
                  form={FORMS.VEHICLE.VEH.form}
                  module={MODULES.Master}
                  section={FORMS.VEHICLE}
                  authenticated={true}
                  editMode={editMode}
                  create={!editMode}
                />
              ) : tab === lastViableTab && !isLoading ? (
                <Button
                  type="submit"
                  template="create"
                  isLoading={isSubmitLoading}
                  form={FORMS.VEHICLE.VEH.form}
                  module={MODULES.Master}
                  section={FORMS.VEHICLE}
                  authenticated={true}
                  editMode={editMode}
                  create={!editMode}
                />
              ) : (
                ""
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
    <Modal ref={deleteWarningRef} closeButton={false} minWidth={"300px"}>
            {showDeleteWarning && deleteWarningModal}
          </Modal>
    </>
  );
};

export default AddVehicle;
